// Layouts/Profile/ProfileView.tsx
import React, { useState, useEffect } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import EditProfileModal from './parts/EditProfileModal';
import { G, C } from '../../components/GlobalComponents/uiTokens';
import {
  Pencil,
  Mail,
  Phone,
  MapPin,
  Users,
  Building2,
  Calendar,
  Shield,
  User as UserIcon,
  Hash,
} from 'lucide-react';

// ── Types (mirrors buildProfileResponse in profileController.go) ─────────────
export interface AgencyInfo {
  id: string;
  agency_code: string;
  name: string;
  description: string | null;
  state: string;
  city: string;
  pincode: string;
  created_at: string | null;
  updated_at: string | null;
}
export interface Profile {
  id: string;
  email: string | null;
  role: string | null;
  name: string | null;
  gender: string | null;
  address: string | null;
  contact_number: string | null;
  agency_id: string | null;
  agency_code: string | null;
  created_at: string | null;
  updated_at: string | null;
  agency: AgencyInfo | null;
}

// ── SHELL / INNER (matches Leads/LeadDetailView chrome) ───────────────────────
const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

const AVCOLORS = ['#115550', '#16706a', '#1a8a82', '#2563eb', '#7c3aed', '#db2777', '#ea580c'];
const avc = (s: string | null) => {
  if (!s) return AVCOLORS[0];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return AVCOLORS[Math.abs(h) % AVCOLORS.length];
};
const getInitials = (name: string | null, email: string | null) => {
  if (name && name.trim()) {
    const p = name.trim().split(' ');
    return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
  }
  return email ? email.substring(0, 2).toUpperCase() : '?';
};
const fmtDate = (d: string | null) => {
  if (!d) return '—';
  try {
    // backend sends "2006-01-02 15:04:05"
    return new Date(d.replace(' ', 'T') + 'Z').toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return d;
  }
};

const ROLE_STYLES: Record<string, { bg: string; color: string; border: string }> = {
  admin: { bg: 'rgba(124,58,237,0.1)', color: '#6d28d9', border: 'rgba(124,58,237,0.3)' },
  staff: { bg: 'rgba(37,99,235,0.1)', color: '#1d4ed8', border: 'rgba(37,99,235,0.3)' },
};
const getRoleStyle = (r: string | null) =>
  ROLE_STYLES[(r || '').toLowerCase()] ?? { bg: 'rgba(156,163,175,0.12)', color: '#4b5563', border: 'rgba(156,163,175,0.3)' };

// ── Card + row helpers ─────────────────────────────────────────────────────────
const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={SHELL}>
    <div style={{ ...INNER, padding: '16.6px 18.5px', ...style }}>{children}</div>
  </div>
);

const SectionHeader: React.FC<{ icon: React.ComponentType<{ size?: number; color?: string }>; title: string }> = ({ icon: Icon, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px', marginBottom: '14.8px' }}>
    <div style={{ width: '3px', height: '14.8px', background: `linear-gradient(180deg,${C.lime},#8aaa00)`, borderRadius: '3px' }} />
    <div style={{ width: '27px', height: '27px', background: C.lime, borderRadius: '7.4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={13} color={C.teal} />
    </div>
    <span style={{ fontSize: '0.81rem', fontWeight: 700, color: C.teal }}>{title}</span>
  </div>
);

const InfoRow: React.FC<{ icon: React.ComponentType<{ size?: number; color?: string }>; label: string; value: string | null }> = ({
  icon: Icon,
  label,
  value,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', padding: '8.3px 0', borderBottom: `1px solid ${G.softer}` }}>
    <div style={{ width: '26px', height: '26px', background: C.limeDim, borderRadius: '6.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={12} color={C.teal} />
    </div>
    <span style={{ fontSize: '0.67rem', color: C.muted, fontWeight: 600, width: '90px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
      {label}
    </span>
    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1f2937', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {value || '—'}
    </span>
  </div>
);

// ── MAIN COMPONENT ──────────────────────────────────────────────────────────
const ProfileView: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get('/v1/profile');
      if (res.data.success) setProfile(res.data.data);
      else setError(res.data.error || 'Failed to load profile');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleSaved = (updated: Partial<Profile>) => {
    setProfile((prev) => (prev ? { ...prev, ...updated } : prev));
    toast.current?.show({ severity: 'success', summary: 'Profile Updated', life: 2500 });
  };

  if (loading && !profile)
    return (
      <div style={SHELL}>
        <div style={{ ...INNER, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '340px' }}>
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner style={{ width: '37px', height: '37px' }} strokeWidth="3" animationDuration=".8s" />
            <p style={{ marginTop: '9.2px', color: C.teal, fontSize: '0.81rem', fontWeight: 500 }}>Loading profile…</p>
          </div>
        </div>
      </div>
    );

  if (error || !profile)
    return (
      <div style={SHELL}>
        <div style={{ ...INNER, padding: '22px' }}>
          <Message severity="error" text={error || 'Profile not found'} style={{ width: '100%', fontSize: '0.83rem' }} />
        </div>
      </div>
    );

  const rs = getRoleStyle(profile.role);
  const initials = getInitials(profile.name, profile.email);
  const avatarColor = avc(profile.name || profile.email);

  return (
    <div style={{ margin: '0 auto' }}>
      <Toast ref={toast} />

      <EditProfileModal
        visible={showEdit}
        onHide={() => setShowEdit(false)}
        profile={profile}
        onSaved={handleSaved}
      />

      {/* ── HEADER CARD ── */}
      <div style={{ ...SHELL, marginBottom: '11px' }}>
        <div style={{ ...INNER, padding: '18.5px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '140px', height: '140px', background: 'rgba(200,233,75,0.12)', borderRadius: '50%', filter: 'blur(35px)', pointerEvents: 'none' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '13px', position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
              <div
                style={{
                  width: '54px',
                  height: '54px',
                  borderRadius: '50%',
                  background: avatarColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  flexShrink: 0,
                  boxShadow: `0 4px 14px ${avatarColor}55`,
                }}
              >
                {initials}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', flexWrap: 'wrap' }}>
                  <h1 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: C.teal, letterSpacing: '-0.2px' }}>
                    {profile.name || 'Unnamed User'}
                  </h1>
                  <span
                    style={{
                      padding: '2.8px 8.3px',
                      borderRadius: '20px',
                      fontSize: '0.63rem',
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      background: rs.bg,
                      color: rs.color,
                      border: `1px solid ${rs.border}`,
                    }}
                  >
                    {profile.role || 'user'}
                  </span>
                </div>
                <span style={{ fontSize: '0.78rem', color: C.sub }}>{profile.email}</span>
              </div>
            </div>

            <button
              onClick={() => setShowEdit(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4.6px',
                padding: '7.4px 15px',
                background: C.limeDim,
                border: `1px solid ${C.limeBorder}`,
                borderRadius: '7.4px',
                color: '#4a5e00',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(200,233,75,0.3)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = C.limeDim; }}
            >
              <Pencil size={13} /> EDIT PROFILE
            </button>
          </div>
        </div>
      </div>

      {/* ── GRID ── */}
      <div style={{ display: 'grid', gridTemplateColumns: profile.agency ? '1fr 1fr' : '1fr', gap: '11px' }}>
        <Card>
          <SectionHeader icon={UserIcon} title="Personal Details" />
          <InfoRow icon={Mail} label="Email" value={profile.email} />
          <InfoRow icon={Phone} label="Contact No." value={profile.contact_number} />
          <InfoRow icon={Users} label="Gender" value={profile.gender ? profile.gender[0].toUpperCase() + profile.gender.slice(1) : null} />
          <InfoRow icon={MapPin} label="Address" value={profile.address} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', padding: '8.3px 0' }}>
            <div style={{ width: '26px', height: '26px', background: C.limeDim, borderRadius: '6.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Calendar size={12} color={C.teal} />
            </div>
            <span style={{ fontSize: '0.67rem', color: C.muted, fontWeight: 600, width: '90px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Joined
            </span>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1f2937', flex: 1 }}>{fmtDate(profile.created_at)}</span>
          </div>
        </Card>

        {profile.agency && (
          <Card>
            <SectionHeader icon={Building2} title="Agency" />
            <InfoRow icon={Hash} label="Code" value={profile.agency.agency_code} />
            <InfoRow icon={Building2} label="Name" value={profile.agency.name} />
            <InfoRow icon={MapPin} label="City" value={profile.agency.city} />
            <InfoRow icon={MapPin} label="State" value={profile.agency.state} />
            <InfoRow icon={Shield} label="Pincode" value={profile.agency.pincode} />
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProfileView;